// ============================================================================
//  ballSeams — czysta matematyka szwów piłki (bez three/DOM → testowalna w Node).
//
//  Prawdziwa piłka ma 8 paneli wyznaczonych przez 4 okręgi na sferze:
//   • równik          — wielki okrąg, normalna (0,1,0),
//   • południk        — wielki okrąg przez bieguny, normalna (1,0,0),
//   • 2 boczne okręgi — małe okręgi wokół osi ±X o promieniu kątowym
//                       SIDE_CIRCLE_ANGLE (klasyczne „łezki" po bokach).
//
//  Szwy liczymy per-piksel: UV → kierunek na sferze → odległość kątowa od
//  najbliższego okręgu → maska z analitycznym antyaliasingiem (smoothstep).
//  Sinusoidy w UV (poprzednie podejście) NIE są okręgami na sferze — stąd
//  wcześniejsze „krzywe" szwy.
// ============================================================================

export const SIDE_CIRCLE_ANGLE = (53 * Math.PI) / 180

// UV → punkt na sferze jednostkowej. Konwencja three.js SphereGeometry
// (flipY tekstury canvas): u wokół osi Y, v=1 → biegun północny (góra obrazu).
export function dirFromUV(u, v) {
  const phi = u * 2 * Math.PI
  const theta = (1 - v) * Math.PI
  const st = Math.sin(theta)
  return [-Math.cos(phi) * st, Math.cos(theta), Math.sin(phi) * st]
}

// Odległość kątowa (rad) punktu p (jednostkowego) od najbliższego okręgu szwu.
export function seamDistance(p, alpha = SIDE_CIRCLE_ANGLE) {
  const x = clamp1(p[0])
  const y = clamp1(p[1])

  const dEquator = Math.abs(Math.asin(y))          // wielki okrąg, normalna Y
  const dMeridian = Math.abs(Math.asin(x))         // wielki okrąg, normalna X

  const ax = Math.acos(x)                          // kąt do bieguna +X
  const dSide = Math.min(
    Math.abs(ax - alpha),                          // okrąg wokół +X
    Math.abs(ax - (Math.PI - alpha)),              // okrąg wokół −X
  )

  return Math.min(dEquator, dMeridian, dSide)
}

// Maska szwów W×H (Float32Array, 0..1): 1 = środek szwu, 0 = panel.
// width — pełna szerokość szwu w radianach, aa — pas antyaliasingu (rad).
export function buildSeamMask(W, H, { width, aa, alpha = SIDE_CIRCLE_ANGLE }) {
  const mask = new Float32Array(W * H)
  const half = width / 2
  for (let row = 0; row < H; row++) {
    const v = 1 - (row + 0.5) / H
    for (let col = 0; col < W; col++) {
      const u = (col + 0.5) / W
      const d = seamDistance(dirFromUV(u, v), alpha)
      mask[row * W + col] = 1 - smoothstep(half - aa, half + aa, d)
    }
  }
  return mask
}

function clamp1(v) {
  return Math.min(1, Math.max(-1, v))
}

function smoothstep(e0, e1, x) {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
  return t * t * (3 - 2 * t)
}
